import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  analyticsRidesFilterApplied,
  analyticsRidesFilterCleared,
  analyticsRidesPageChanged,
  analyticsSearchedRides,
} from '@rideiq/api/services/Analytics';
import { getRides } from '@rideiq/api/services/Rides';
import * as Constants from '@rideiq/constants';
import { useTimezone } from '@rideiq/hooks/useTimezone';
import { createRidesURLParams, readRidesURLState, RIDES_FILTER_URL_KEYS } from '@rideiq/rides/hooks/ridesURLParams';
import { useRidesTablePrefsStore } from '@rideiq/rides/stores/ridesTablePrefsStore';
import type { GroupedRides, RidesQueryParams } from '@rideiq/rides/types';
import { formatDate } from '@rideiq/utils/helpers';
import { useQuery } from '@tanstack/react-query';
import { isToday, isTomorrow } from 'date-fns';
import { debounce, groupBy } from 'lodash';

export const RIDES_PAGINATED_QUERY_KEY = 'rides-paginated';

const getSortDirection = (state: string): 'asc' | 'desc' => (['all', 'requested'].includes(state) ? 'asc' : 'desc');

const getDateGroup = (date: Date) =>
  (isToday(date) && `TODAY ${formatDate(date, 'LL.dd.yy').toUpperCase()}`) ||
  (isTomorrow(date)
    ? `TOMORROW ${formatDate(date, 'LL.dd.yy').toUpperCase()}`
    : !isToday(date) && !isTomorrow(date) && formatDate(date, 'EEEE LL.dd.yy').toUpperCase());

const buildApiParams = (params: RidesQueryParams) => ({
  perPage: params.perPage,
  page: params.page,
  ...(params.query && { q: params.query }),
  state: params.state,
  fromDate: params.fromDate,
  sortDir: params.sortDir,
  ...(params.accountLocationIds?.length && { accountLocationIds: params.accountLocationIds }),
  ...(params.tagNames?.length && { tagNames: params.tagNames }),
  ...(params.serviceIds?.length && { serviceIds: params.serviceIds }),
  ...(params.sharedBillingRoles?.length && { sharedBillingRoles: params.sharedBillingRoles }),
});

const parseIdsParam = (value: string | null): string[] => (value ? value.split(',').filter(Boolean) : []);

const areEqual = (left: string[] = [], right: string[] = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export const useRidesPaginatedQuery = () => {
  const location = useLocation();
  const locationRef = useRef(location);

  locationRef.current = location;
  const [, setURLSearchParams] = useSearchParams();
  const { getDateInTimezone, getEndOfDate } = useTimezone();
  const URLqueryParams = new URLSearchParams(location.search);
  const urlState = readRidesURLState(location.search, getEndOfDate);
  const initialState = urlState.state;
  const perPage = useRidesTablePrefsStore((state) => state.perPage);
  const setPersistedPerPage = useRidesTablePrefsStore((state) => state.setPerPage);

  const [queryParams, setQueryParams] = useState<RidesQueryParams>({
    page: 1,
    perPage,
    query: urlState.query,
    state: initialState,
    sortDir: getSortDirection(initialState),
    accountLocationIds: parseIdsParam(URLqueryParams.get('accountLocationIds')),
    tagNames: parseIdsParam(URLqueryParams.get('tagNames')),
    serviceIds: parseIdsParam(URLqueryParams.get('serviceIds')),
    sharedBillingRoles: parseIdsParam(URLqueryParams.get('sharedBillingRoles')),
    ...(urlState.fromDate && { fromDate: urlState.fromDate }),
  });
  const queryParamsRef = useRef(queryParams);

  queryParamsRef.current = queryParams;

  const { data, isFetching, isError, isLoadingError, refetch, dataUpdatedAt } = useQuery({
    queryKey: [RIDES_PAGINATED_QUERY_KEY, queryParams],
    queryFn: async ({ signal }) => {
      const response = await getRides(buildApiParams(queryParams), signal);

      if (response.type === 'error') {
        throw new Error(response.error.errors[0] ?? 'Failed to load rides');
      }

      if (queryParams.query) {
        analyticsSearchedRides(queryParams.query, response.data.trips?.length ?? 0);
      }

      return response.data;
    },
  });

  const rides = useMemo<GroupedRides>(() => {
    if (!data) {
      return {};
    }

    const newRides = (data.trips ?? []).filter((trip) => trip.passengers.length > 0);
    const newGroupedRides = groupBy(newRides, (ride) => getDateGroup(getDateInTimezone(ride.startsAt)));
    const fromDateGroup = queryParams.fromDate
      ? { [getDateGroup(getDateInTimezone(queryParams.fromDate)) || '']: [] }
      : {};

    return { ...fromDateGroup, ...newGroupedRides };
  }, [data, queryParams.fromDate, getDateInTimezone]);

  const totalCount = data?.meta?.totalCount ?? data?.trips?.length ?? 0;

  const isLoading = !data && !isError;

  const lastFetch = useMemo(() => (dataUpdatedAt ? new Date(dataUpdatedAt) : undefined), [dataUpdatedAt]);

  const handlePageChange = useCallback(
    (params: { page: number; perPage: number }) => {
      const totalPages = Math.ceil(totalCount / params.perPage);

      analyticsRidesPageChanged(params.page, totalPages, totalCount);
      if (params.perPage !== queryParams.perPage) {
        setPersistedPerPage(params.perPage);
      }
      setQueryParams((prev) => ({ ...prev, page: params.page, perPage: params.perPage }));
    },
    [totalCount, queryParams.perPage, setPersistedPerPage]
  );

  const refreshAfterBulkCancel = useCallback(() => {
    if (queryParams.page === 1) {
      refetch();

      return;
    }

    setQueryParams((prev) => ({ ...prev, page: 1 }));
  }, [queryParams.page, refetch]);

  const handleQuery = useCallback(
    (query: string, state?: string, fromDate?: string | null, silentReFetching = false) => {
      if (!query || query.length >= Constants.MINIMUM_SEARCH_STRING_LENGTH || silentReFetching) {
        window.scrollTo(0, 0);
        setQueryParams((prevQueryParams) => {
          const nextState = state || prevQueryParams.state;
          const isTabSwitch = nextState !== prevQueryParams.state;

          return {
            query: query !== prevQueryParams.query ? query : prevQueryParams.query,
            page: 1,
            perPage: prevQueryParams.perPage,
            state: nextState,
            fromDate: fromDate !== prevQueryParams.fromDate ? fromDate : prevQueryParams.fromDate,
            sortDir: getSortDirection(nextState),
            accountLocationIds: isTabSwitch ? [] : prevQueryParams.accountLocationIds ?? [],
            tagNames: isTabSwitch ? [] : prevQueryParams.tagNames ?? [],
            serviceIds: isTabSwitch ? [] : prevQueryParams.serviceIds ?? [],
            sharedBillingRoles: isTabSwitch ? [] : prevQueryParams.sharedBillingRoles ?? [],
          };
        });
      }
    },
    []
  );

  const setActiveFilters = useCallback((selections: Record<string, string[]>) => {
    const accountLocationIds = selections.accountLocationIds ?? [];
    const tagNames = selections.tagNames ?? [];
    const serviceIds = selections.serviceIds ?? [];
    const sharedBillingRoles = selections.sharedBillingRoles ?? [];

    const activeSelections = Object.fromEntries(
      Object.entries({ accountLocationIds, tagNames, serviceIds, sharedBillingRoles }).filter(
        ([, values]) => values.length > 0
      )
    );

    if (Object.keys(activeSelections).length > 0) {
      analyticsRidesFilterApplied(activeSelections);
    }

    setQueryParams((prev) => ({ ...prev, accountLocationIds, tagNames, serviceIds, sharedBillingRoles, page: 1 }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setQueryParams((prev) => {
      const previousFilters: Record<string, string[]> = {
        accountLocationIds: prev.accountLocationIds ?? [],
        tagNames: prev.tagNames ?? [],
        serviceIds: prev.serviceIds ?? [],
        sharedBillingRoles: prev.sharedBillingRoles ?? [],
      };

      if (Object.values(previousFilters).some((values) => values.length > 0)) {
        analyticsRidesFilterCleared(previousFilters);
      }

      return { ...prev, accountLocationIds: [], tagNames: [], serviceIds: [], sharedBillingRoles: [], page: 1 };
    });
  }, []);

  const debounceHandleQuery = useMemo(() => debounce(handleQuery, 400), [handleQuery]);

  useEffect(() => {
    return () => {
      debounceHandleQuery.cancel();
    };
  }, [debounceHandleQuery]);

  // avoids feedback loop with the write effect below (RIQ-981)
  const skipURLWrite = useRef(false);
  const hasWrittenURL = useRef(false);

  useEffect(() => {
    const nextURLState = readRidesURLState(location.search, getEndOfDate);
    const params = new URLSearchParams(location.search);
    const accountLocationIds = parseIdsParam(params.get('accountLocationIds'));
    const tagNamesParam = params.get('tagNames') || '';
    const tagNames = parseIdsParam(tagNamesParam);
    const serviceIds = parseIdsParam(params.get('serviceIds'));
    const sharedBillingRoles = parseIdsParam(params.get('sharedBillingRoles'));
    const currentQueryParams = queryParamsRef.current;

    // Serialized equality keeps commas inside selected tag names intact after our own URL write.
    if (
      currentQueryParams.query === nextURLState.query &&
      currentQueryParams.state === nextURLState.state &&
      (currentQueryParams.fromDate || undefined) === nextURLState.fromDate &&
      areEqual(currentQueryParams.accountLocationIds, accountLocationIds) &&
      (areEqual(currentQueryParams.tagNames, tagNames) ||
        (currentQueryParams.tagNames ?? []).join(',') === tagNamesParam) &&
      areEqual(currentQueryParams.serviceIds, serviceIds) &&
      areEqual(currentQueryParams.sharedBillingRoles, sharedBillingRoles)
    ) {
      return;
    }

    skipURLWrite.current = true;
    window.scrollTo(0, 0);
    setQueryParams((previous) => {
      if (
        previous.query === nextURLState.query &&
        previous.state === nextURLState.state &&
        previous.fromDate === nextURLState.fromDate &&
        areEqual(previous.accountLocationIds, accountLocationIds) &&
        areEqual(previous.tagNames, tagNames) &&
        areEqual(previous.serviceIds, serviceIds) &&
        areEqual(previous.sharedBillingRoles, sharedBillingRoles)
      ) {
        return previous;
      }

      return {
        ...previous,
        query: nextURLState.query,
        state: nextURLState.state,
        fromDate: nextURLState.fromDate,
        sortDir: getSortDirection(nextURLState.state),
        page: 1,
        accountLocationIds,
        tagNames,
        serviceIds,
        sharedBillingRoles,
      };
    });
  }, [getEndOfDate, location.search]);

  // Only filter fields write to history; pagination and URL changes must not create entries.
  useEffect(() => {
    const replaceInitialURL = !hasWrittenURL.current;

    hasWrittenURL.current = true;

    if (skipURLWrite.current) {
      skipURLWrite.current = false;

      return;
    }

    const current = new URLSearchParams(locationRef.current.search);
    const next = createRidesURLParams(locationRef.current.search, queryParams, RIDES_FILTER_URL_KEYS);

    if (next.toString() === current.toString()) {
      return;
    }

    setURLSearchParams(next, replaceInitialURL ? { replace: true } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryParams.state,
    queryParams.query,
    queryParams.fromDate,
    queryParams.accountLocationIds,
    queryParams.tagNames,
    queryParams.serviceIds,
    queryParams.sharedBillingRoles,
  ]);

  return {
    rides,
    totalCount,
    queryParams,
    isLoading,
    isFetching,
    isError,
    isLoadingError,
    lastFetch,
    fetchRides: refetch,
    handlePageChange,
    refreshAfterBulkCancel,
    debounceHandleQuery,
    setActiveFilters,
    clearAllFilters,
  };
};
